/**
 * Cobertura (layout del rediseño AI Studio, 2026-10-15; antes UI v3 UI-5, DESIGN.md §5 y §9). Con sesión vive
 * dentro de la app (misma barra lateral); sin sesión, o con `?publica`, es la página pública con el mismo
 * contenido. Las cifras salen solo de GET /api/coverage (D-UI-7): totales, desglose, años y última carga. Las notas,
 * cómo se actualiza cada biblioteca y «¿Qué no incluye?» son editoriales (data/coverage.ts). La barra de años dibuja
 * solo los años con documentos: la API no distingue «parcial» ni «en carga», así que la leyenda tampoco.
 */
import { Fragment, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, ExternalLink, FileText, Gavel, Globe, Info, Scale, ShieldCheck, X } from 'lucide-react';
import Layout from '../components/layout/Layout';
import Logo from '../components/shared/Logo';
import ThemeToggle from '../components/layout/ThemeToggle';
import { Columna, FOCO, Relleno } from '../components/v3/Ajustes';
import { useAuth } from '../contexts/AuthContext';
import { getCoverage, CoverageData, CoverageRow } from '../services/coverage.service';
import { COVERAGE_EDITORIAL, NOT_COVERED_CHIPS } from '../data/coverage';
import { Collection, CollectionKey, getAllCollections } from '../utils/institutions';
import { tipoPlural } from '../utils/documentoMeta';
import { diaRelativo, fechaCorta, hora } from '../utils/fechas';

const ICONOS: Record<CollectionKey, typeof FileText> = { adm: FileText, jud: Gavel, ley: Scale, conv: Globe };
const ABIERTAS_AL_INICIO: CollectionKey[] = ['adm'];
const nf = (n: number) => n.toLocaleString('es-CL');
const BARRA = 'bg-[#818CF8]';
const ACCION = `text-[12px] font-medium text-[#4F46E5] hover:underline inline-flex items-center gap-0.5 rounded ${FOCO}`;

interface Resumen {
  total: number;
  sinTexto: number;
  years: number[];
  min: number | null;
  max: number | null;
  ultimaCarga: string | null;
  porTipo: { tipo: string; total: number; desde: number | null }[];
}

/** Agrega las filas de coverage_stats de una colección (misma institución y sus tipos). */
export function resumenColeccion(col: Collection, rows: CoverageRow[]): Resumen | null {
  const propias = rows.filter(r => r.institution_code.toUpperCase() === col.institution && col.tipos.includes(r.tipo.toLowerCase()));
  if (!propias.length) return null;
  const years = Array.from(new Set(propias.flatMap(r => r.years || []))).sort((a, b) => a - b);
  const ultimaCarga = propias.reduce<string | null>((m, r) => (r.last_ingest_at && (!m || r.last_ingest_at > m) ? r.last_ingest_at : m), null);
  return {
    total: propias.reduce((s, r) => s + r.total, 0),
    sinTexto: propias.reduce((s, r) => s + (r.sin_texto || 0), 0),
    years,
    min: years.length ? years[0] : null,
    max: years.length ? years[years.length - 1] : null,
    ultimaCarga,
    porTipo: col.tipos
      .map(t => { const r = propias.find(x => x.tipo.toLowerCase() === t); return { tipo: t, total: r?.total || 0, desde: r?.year_min ?? null }; })
      .filter(x => x.total > 0),
  };
}

/** «Oficios, circulares y resoluciones desde 2010.» o «Oficios desde 2010; circulares desde 2013.»: el primer año de cada tipo, de los datos. */
export function notaDesde(porTipo: Resumen['porTipo']): string | null {
  const grupos: { desde: number; tipos: string[] }[] = [];
  porTipo.filter(t => t.desde != null).forEach(t => {
    const g = grupos.find(x => x.desde === t.desde);
    if (g) g.tipos.push(tipoPlural(t.tipo).toLowerCase());
    else grupos.push({ desde: t.desde as number, tipos: [tipoPlural(t.tipo).toLowerCase()] });
  });
  if (!grupos.length) return null;
  const lista = (xs: string[]) => (xs.length > 1 ? `${xs.slice(0, -1).join(', ')} y ${xs[xs.length - 1]}` : xs[0]);
  const texto = grupos.sort((a, b) => a.desde - b.desde).map(g => `${lista(g.tipos)} desde ${g.desde}`).join('; ');
  return `${texto.charAt(0).toUpperCase()}${texto.slice(1)}.`;
}

/** Tramos de años seguidos: [2010, 2011, 2013, 2014] → [[2010, 2011], [2013, 2014]]. */
export function tramos(years: number[]): [number, number][] {
  const out: [number, number][] = [];
  years.forEach(y => {
    const last = out[out.length - 1];
    if (last && y === last[1] + 1) last[1] = y;
    else out.push([y, y]);
  });
  return out;
}

const rango = ([a, b]: [number, number]) => (a === b ? `${a}` : `${a}–${b}`);

/** Escala de la barra: `years` marca cada año con documentos (los huecos se ven); `range` une el primero y el último. */
function escala(years: number[], modo: 'years' | 'range', hasta: number) {
  const segs = modo === 'years' ? tramos(years) : [[years[0], years[years.length - 1]] as [number, number]];
  const desde = modo === 'years' ? Math.min(2010, years[0]) : Math.floor(years[0] / 10) * 10;
  const fin = Math.max(hasta, years[years.length - 1]);
  const x = (y: number) => ((y - desde) / (fin + 1 - desde)) * 100;
  return { segs, desde, fin, x };
}

/** Barra de años: tramos con documentos sobre la pista gris. `mini` = la versión de la fila plegada, sin rótulos. */
function BarraAnios({ years, modo, hasta, mini = false }: { years: number[]; modo: 'years' | 'range'; hasta: number; mini?: boolean }) {
  const { segs, desde, fin, x } = escala(years, modo, hasta);
  const medio = Math.round((desde + fin) / 2);
  const pista = (
    <div
      className={`relative h-1.5 rounded-full bg-slate-100 overflow-hidden ${mini ? 'w-28' : 'w-full'}`}
      role={mini ? undefined : 'img'}
      aria-hidden={mini || undefined}
      aria-label={mini ? undefined : `Años con documentos: ${segs.map(rango).join(', ')}`}
    >
      {segs.map(s => (
        <i key={s[0]} title={rango(s)} className={`absolute top-0 h-full ${BARRA}`} style={{ left: `${x(s[0])}%`, width: `${x(s[1] + 1) - x(s[0])}%` }} />
      ))}
    </div>
  );
  if (mini) return pista;
  return (
    <div className="w-full max-w-[380px]">
      {pista}
      <div className="relative h-4 mt-1 text-[10.5px] text-slate-500 tabular-nums" aria-hidden="true">
        <span className="absolute left-0">{desde}</span>
        <span className="absolute -translate-x-1/2" style={{ left: `${x(medio) + (x(medio + 1) - x(medio)) / 2}%` }}>{medio}</span>
        <span className="absolute right-0">{fin}</span>
      </div>
    </div>
  );
}

/** Fila de detalle: rótulo a la izquierda (3/12) y contenido a la derecha; en móvil, uno bajo el otro. */
function Detalle({ rotulo, children, arriba = false }: { rotulo: string; children: ReactNode; arriba?: boolean }) {
  return (
    <div className={`py-2.5 grid grid-cols-1 sm:grid-cols-12 gap-x-4 gap-y-1 ${arriba ? 'items-start' : 'items-baseline'}`}>
      <div className="sm:col-span-3 text-slate-500 font-medium">{rotulo}</div>
      <div className="sm:col-span-9 min-w-0">{children}</div>
    </div>
  );
}

function FilaColeccion({ col, datos, estado, enApp, hasta, abierta, onToggle }: {
  col: Collection; datos: Resumen | null; estado: 'cargando' | 'error' | 'listo'; enApp: boolean; hasta: number; abierta: boolean; onToggle: () => void;
}) {
  const Icono = ICONOS[col.key];
  const ed = COVERAGE_EDITORIAL[col.key];
  const notaAnios = ed.desdePorTipo && datos ? notaDesde(datos.porTipo) : ed.yearsNote;
  const cuerpo = `cob-${col.key}`;
  return (
    <section className="py-4">
      <h2>
        <button
          type="button"
          aria-expanded={abierta}
          aria-controls={abierta ? cuerpo : undefined}
          onClick={onToggle}
          className={`w-full flex items-center justify-between gap-4 text-left group rounded-lg ${FOCO}`}
        >
          <span className="flex items-center gap-3.5 min-w-0">
            <span className="w-9 h-9 rounded-xl bg-slate-100/80 text-slate-600 group-hover:bg-[#EEF2FF] group-hover:text-[#4F46E5] flex items-center justify-center shrink-0 transition-colors motion-reduce:transition-none" aria-hidden="true">
              <Icono className="w-4 h-4" />
            </span>
            <span className="min-w-0">
              <span className="block text-[14.5px] font-bold text-[#142D4E]">{col.label}</span>
              <span className="block text-[12px] text-slate-500 truncate">{col.description}</span>
            </span>
          </span>
          <span className="flex items-center gap-4 shrink-0 text-[13px]">
            {estado === 'cargando' ? (
              <span className="w-16"><Relleno ancho="100%" /></span>
            ) : datos && (
              <>
                {!abierta && datos.years.length > 0 && (
                  <span className="hidden sm:flex"><BarraAnios years={datos.years} modo={ed.bar} hasta={hasta} mini /></span>
                )}
                <span className="font-bold text-[#142D4E] tabular-nums min-w-[3.5rem] text-right">{nf(datos.total)}</span>
                <span className="hidden sm:inline text-slate-500 text-[12px] min-w-[40px] text-right" title="Última carga">{diaRelativo(datos.ultimaCarga)}</span>
              </>
            )}
            {abierta
              ? <ChevronDown className="w-4 h-4 text-slate-400" aria-hidden="true" />
              : <ChevronRight className="w-4 h-4 text-slate-400" aria-hidden="true" />}
          </span>
        </button>
      </h2>

      {abierta && (
        <div id={cuerpo} className="mt-4 sm:pl-12">
          {estado === 'cargando' && (
            <div className="border-t border-b border-slate-100 py-3 space-y-2"><Relleno ancho="55%" /><Relleno ancho="35%" /></div>
          )}
          {datos && (
            <div className="divide-y divide-slate-100 border-t border-slate-100 text-[12.5px]">
              {datos.years.length > 0 && (
                <Detalle rotulo={`Años (${datos.min === datos.max ? datos.min : `${datos.min}–${datos.max}`})`} arriba>
                  <div className="space-y-1.5">
                    <BarraAnios years={datos.years} modo={ed.bar} hasta={hasta} />
                    {notaAnios && <p className="text-[12px] text-slate-500 leading-relaxed">{notaAnios}</p>}
                  </div>
                </Detalle>
              )}
              {datos.porTipo.length > 1 && (
                <Detalle rotulo="Desglose">
                  <span className="text-[12px] text-[#142D4E] tabular-nums">
                    {datos.porTipo.map((t, i) => (
                      <Fragment key={t.tipo}>{i > 0 && ' · '}<span><strong>{nf(t.total)}</strong> {tipoPlural(t.tipo)}</span></Fragment>
                    ))}
                  </span>
                  {datos.porTipo.some(t => ed.tipoNotes?.[t.tipo]) && (
                    <p className="text-[12px] text-slate-500 mt-1">
                      {datos.porTipo.filter(t => ed.tipoNotes?.[t.tipo]).map(t => `${tipoPlural(t.tipo)}: ${ed.tipoNotes![t.tipo]}`).join(' ')}
                    </p>
                  )}
                </Detalle>
              )}
              {datos.sinTexto > 0 && (
                <Detalle rotulo="Sin texto extraído">
                  <span className="text-slate-600"><strong className="text-[#142D4E] tabular-nums">{nf(datos.sinTexto)}</strong> · Escaneados: se listan en la biblioteca, pero su texto aún no se extrae.</span>
                </Detalle>
              )}
            </div>
          )}
          {(ed.note || ed.latest || ed.update) && (
            <div className={`divide-y divide-slate-100 border-b border-slate-100 text-[12.5px] ${datos ? '' : 'border-t'}`}>
              {[ed.note, ed.latest].map(n => n && (
                <Detalle key={n.label} rotulo={n.label}>
                  <span className="text-slate-600"><strong className="text-[#142D4E]">{n.value}</strong>{n.note && <> · {n.note}</>}</span>
                </Detalle>
              ))}
              {ed.update && (
                <Detalle rotulo="Actualización">
                  <span className="text-slate-600"><strong className="text-[#142D4E]">{ed.update.value}</strong>{ed.update.note && <> · {ed.update.note}</>}</span>
                </Detalle>
              )}
            </div>
          )}
          {enApp && (
            <div className="mt-2.5">
              <Link to={`/biblioteca/${col.key}`} className={ACCION}>
                <span>Abrir biblioteca</span> <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />
              </Link>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/** «¿Qué no incluye?»: botón con la lista editorial en una ventana; se cierra con Escape, con ✕ o al hacer clic fuera. */
function QueNoIncluye({ siiDesde }: { siiDesde: number | null }) {
  const [abierto, setAbierto] = useState(false);
  const caja = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent) => { if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false); };
    const tecla = (e: KeyboardEvent) => { if (e.key === 'Escape') setAbierto(false); };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', tecla);
    return () => { document.removeEventListener('mousedown', fuera); document.removeEventListener('keydown', tecla); };
  }, [abierto]);
  return (
    <div className="relative" ref={caja}>
      <button
        type="button"
        aria-expanded={abierto}
        aria-controls="cob-no-incluye"
        onClick={() => setAbierto(a => !a)}
        className={`px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-[12px] font-medium text-slate-600 hover:text-[#142D4E] flex items-center gap-1.5 transition-colors motion-reduce:transition-none ${FOCO}`}
      >
        <Info className="w-3.5 h-3.5 text-slate-400" aria-hidden="true" />
        <span>¿Qué no incluye?</span>
        <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform motion-reduce:transition-none ${abierto ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {abierto && (
        <div id="cob-no-incluye" className="absolute right-0 mt-1.5 w-72 max-w-[calc(100vw-2rem)] bg-white rounded-2xl border border-slate-200 shadow-xl p-4 z-30 text-[12px] space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10.5px] font-bold uppercase tracking-wider text-rose-700">Fuera del alcance actual</span>
            <button type="button" onClick={() => setAbierto(false)} aria-label="Cerrar" className={`text-slate-400 hover:text-slate-700 rounded ${FOCO}`}>
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <ul className="space-y-1 text-slate-600 leading-relaxed">
            {NOT_COVERED_CHIPS.map(item => <li key={item}>• {item}</li>)}
            {siiDesde != null && <li>• Documentos del SII anteriores a {siiDesde}</li>}
          </ul>
          <p className="text-[11.5px] text-slate-500 pt-1 border-t border-slate-100">Si tu consulta depende de estas fuentes, AlertAI te lo indicará en la respuesta.</p>
        </div>
      )}
    </div>
  );
}

export function ContenidoCobertura({ enApp }: { enApp: boolean }) {
  const [datos, setDatos] = useState<CoverageData | null>(null);
  const [error, setError] = useState(false);
  const colecciones = getAllCollections();
  const [abiertas, setAbiertas] = useState<CollectionKey[]>(ABIERTAS_AL_INICIO);

  const cargar = useCallback(async () => {
    setError(false);
    setDatos(null);
    try {
      setDatos(await getCoverage());
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const estado: 'cargando' | 'error' | 'listo' = error ? 'error' : datos ? 'listo' : 'cargando';
  const rows = datos?.rows || [];
  const ultima = rows.reduce<string | null>((m, r) => (r.last_ingest_at && (!m || r.last_ingest_at > m) ? r.last_ingest_at : m), null);
  const hasta = datos?.computed_at ? new Date(datos.computed_at).getFullYear() : new Date().getFullYear();
  const resumenes = colecciones.map(col => ({ col, datos: resumenColeccion(col, rows) }));
  const total = resumenes.reduce((s, r) => s + (r.datos?.total || 0), 0);
  const todas = abiertas.length === colecciones.length;
  const alternar = (k: CollectionKey) => setAbiertas(a => (a.includes(k) ? a.filter(x => x !== k) : [...a, k]));

  const subtitulo = estado === 'listo' && total > 0
    ? `${nf(total)} documentos oficiales indexados · SII, TTA, BCN y Convenios`
    : 'Normativa tributaria y lo que resuelven sobre ella el SII y los Tribunales Tributarios y Aduaneros';

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <div className="flex items-center gap-3.5 min-w-0 flex-1 basis-[300px]">
          <span className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 bg-[#EEF2FF] text-[#4F46E5]" aria-hidden="true">
            <ShieldCheck className="w-5 h-5" />
          </span>
          <div className="min-w-0">
            <h1 className="text-[16px] font-bold text-[#142D4E] leading-snug">Qué cubre AlertAI y qué no</h1>
            <p className="text-[12.5px] text-slate-500 mt-0.5 leading-snug">{subtitulo}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          <QueNoIncluye siiDesde={resumenes.find(r => r.col.key === 'adm')?.datos?.min ?? null} />
          {ultima && (
            <span className="flex items-center gap-2 text-[12px] font-semibold text-emerald-700 bg-emerald-50/70 px-2.5 py-1 rounded-full">
              <span className="w-2 h-2 rounded-full bg-emerald-500" aria-hidden="true" />
              <span>Última carga {diaRelativo(ultima)} {hora(ultima)}</span>
            </span>
          )}
        </div>
      </div>

      {error && (
        <p className="text-[13px] text-slate-500 mb-3">
          No se pudieron cargar las cifras de cobertura.{' '}
          <button type="button" onClick={cargar} className={`font-medium text-[#4F46E5] hover:underline rounded ${FOCO}`}>Reintentar</button>
        </p>
      )}
      {estado === 'listo' && rows.length === 0 && (
        <p className="text-[13px] text-slate-500 mb-3">Las cifras de cobertura aún no se calculan; aparecerán tras la próxima actualización.</p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 pb-2.5 text-[11.5px]">
        {rows.length > 0 ? (
          <div className="flex items-center gap-4 text-slate-500">
            <span className="flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-[2px] ${BARRA}`} aria-hidden="true" />Años con documentos</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-[2px] bg-slate-100 border border-slate-200" aria-hidden="true" />Sin documentos indexados</span>
          </div>
        ) : <span />}
        <button type="button" onClick={() => setAbiertas(todas ? [] : colecciones.map(c => c.key))} className={`font-medium text-[#4F46E5] hover:underline rounded ${FOCO}`}>
          {todas ? 'Plegar detalles' : `Expandir las ${colecciones.length} bibliotecas`}
        </button>
      </div>

      <div className="divide-y divide-slate-200/70 border-t border-b border-slate-200/70">
        {resumenes.map(({ col, datos: d }) => (
          <FilaColeccion key={col.key} col={col} datos={d} estado={estado} enApp={enApp} hasta={hasta} abierta={abiertas.includes(col.key)} onToggle={() => alternar(col.key)} />
        ))}
      </div>

      <div className="mt-3 space-y-1 text-[11.5px] text-slate-500 leading-relaxed">
        {datos?.computed_at && (
          <p>Cifras calculadas el {fechaCorta(datos.computed_at)} a las {hora(datos.computed_at)}; se recalculan cada día hábil tras la actualización del corpus.</p>
        )}
        <p>Cómo leer una cita: cada afirmación de una respuesta lleva un número; al pasar el mouse se ve el pasaje citado y al hacer clic el documento se abre en Fuentes.</p>
      </div>
    </>
  );
}

export default function Cobertura() {
  const { user, profile, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const publica = new URLSearchParams(location.search).has('publica');

  if (loading) return null;

  // Como ProtectedRoute: una cuenta pendiente o rechazada no entra a la app; ve la versión pública.
  const aprobado = profile?.status !== 'pending' && profile?.status !== 'rejected';
  const pill = `px-3 py-1.5 rounded-xl border text-[12px] flex items-center gap-1.5 transition-colors motion-reduce:transition-none ${FOCO}`;
  if (user && aprobado && !publica) {
    return (
      <Layout
        title="Cobertura"
        actions={
          <Link to="/cobertura?publica" className={`${pill} border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-[#142D4E]`}>
            <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" /> <span>Versión pública</span>
          </Link>
        }
      >
        <Columna ancho={880}>
          <ContenidoCobertura enApp />
        </Columna>
      </Layout>
    );
  }

  const conSesion = !!(user && aprobado);
  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-[876px] mx-auto px-4 sm:px-7 h-[64px] sm:h-[84px] flex items-center justify-between gap-3">
          <Link to="/" aria-label="AlertAI, inicio" className={`rounded ${FOCO}`}><Logo variant="compact" /></Link>
          <div className="flex items-center gap-2">
          <ThemeToggle />
          {conSesion ? (
            <button type="button" onClick={() => navigate('/cobertura')} className={`${pill} border-indigo-200 bg-[#EEF2FF] text-[#4F46E5] font-medium`}>
              Salir de vista pública
            </button>
          ) : (
            <button type="button" onClick={() => navigate('/login')} className={`px-3.5 py-2 rounded-xl text-white text-[12.5px] font-semibold bg-[#4F46E5] hover:bg-indigo-700 transition-colors motion-reduce:transition-none ${FOCO}`}>
              Ingresar
            </button>
          )}
          </div>
        </div>
      </header>
      <main>
        <div className="px-4 sm:px-7 py-6 sm:py-8">
          <div className="mx-auto w-full max-w-[820px]">
            <ContenidoCobertura enApp={false} />
          </div>
        </div>
      </main>
      <footer className="border-t border-slate-100 py-6">
        <p className="max-w-[876px] mx-auto px-4 sm:px-7 text-xs text-slate-500">&copy; 2026 AlertAI</p>
      </footer>
    </div>
  );
}
